import { Add, ArchiveOutlined, Delete, Edit, UnarchiveOutlined } from '@mui/icons-material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardContent,
    Chip,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ClearIcon } from '@mui/x-date-pickers';
import React, { useCallback, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import ActionBubbleTooltip from '../../components/ActionBubbleTooltip';
import DidNotLoad from '../../components/DidNotLoad';
import Loading from '../../components/Loading';
import CustomPagination from '../../components/Pagination';
import PageTutorialButtons from '../../components/tutorials/PageTutorialButtons';
import { quotaBlockedMessage } from '../../components/subscription/QuotaLimitedButton';
import { DEFAULT_LIST_PAGE_SIZE, LIST_PAGE_SIZE_OPTIONS } from '../../constants/listPagination';
import { formatCurrencyInCents } from '../../helpers/formatters';
import {
    OPERATIONAL_COST_CATEGORIES,
    OPERATIONAL_COST_ONE_TIME,
    OPERATIONAL_COST_RECURRING,
    isRecurringOperationalCost,
    operationalCostCategoryLabel,
    operationalCostFrequencyLabel,
    operationalCostMonthlyCents,
    operationalCostQuotaKey,
} from '../../helpers/operationalCosts';
import { buildPaywallUrl } from '../../helpers/paywall';
import { invalidatePlanUsage, usePlanQuota } from '../../helpers/planUsage';
import { useListFetch } from '../../hooks/useListFetch';

const EMPTY_STATS = {
    total_items: 0,
    recurring_total_cents: 0,
    one_time_total_cents: 0,
    month_total_cents: 0,
};

const OPERATIONAL_COSTS_INTRO_TITLE = 'O que registrar nesta tela';
const OPERATIONAL_COSTS_INTRO_BODY =
    'Use este espaço para os custos fixos e operacionais da farm: aluguel, água, internet, marketing, assinaturas, compra de modelos e serviços de terceiros (como modelagem). '
    + 'Custos que ocorrem todos os meses devem ser marcados como recorrentes. '
    + 'Não registre aqui insumos de produção por impressão, como filamento, tintas para acabamento, embalagens, etc. — isso entra na calculadora e nas impressões.';

function OperationalCostsIntroBanner() {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <Box
            sx={{
                width: '100%',
                minHeight: { xs: 120, sm: 132 },
                px: { xs: 2.5, md: 3.25 },
                py: { xs: 2, md: 2.25 },
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                boxSizing: 'border-box',
                mb: 2,
                bgcolor: isDark
                    ? alpha(theme.palette.primary.main, 0.16)
                    : alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.text.primary,
                border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.38 : 0.22)}`,
                borderLeft: `4px solid ${theme.palette.primary.main}`,
            }}
        >
            <Box sx={{ minWidth: 0, width: '100%' }}>
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1.05rem', sm: '1.25rem' },
                        letterSpacing: '-0.04em',
                        lineHeight: 1.12,
                        mb: 0.75,
                        color: theme.palette.text.primary,
                        textTransform: 'uppercase',
                    }}
                >
                    {OPERATIONAL_COSTS_INTRO_TITLE}
                </Typography>
                <Typography
                    sx={{
                        fontWeight: 500,
                        fontSize: { xs: '0.92rem', sm: '1rem' },
                        letterSpacing: '-0.01em',
                        lineHeight: 1.55,
                        color: theme.palette.text.secondary,
                    }}
                >
                    {OPERATIONAL_COSTS_INTRO_BODY}
                </Typography>
            </Box>
        </Box>
    );
}

function CreateOperationalCostButton({ isMobile }) {
    const navigate = useNavigate();
    const location = useLocation();
    const recurring = usePlanQuota('operational_costs_recurring');
    const oneTime = usePlanQuota('operational_costs_one_time');
    const blocked = recurring.blocked && oneTime.blocked;
    const entry = recurring.blocked ? recurring.entry : oneTime.entry;

    if (!blocked) {
        return (
            <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/admin/register-operational-cost')}
                startIcon={<Add />}
                fullWidth={isMobile}
            >
                Custo
            </Button>
        );
    }

    return (
        <ActionBubbleTooltip title={quotaBlockedMessage(entry)}>
            <Button
                variant="contained"
                color="warning"
                startIcon={<LockOutlinedIcon />}
                fullWidth={isMobile}
                onClick={() => navigate(buildPaywallUrl({ returnTo: location.pathname }))}
            >
                Custo
            </Button>
        </ActionBubbleTooltip>
    );
}

function amountDisplay(item) {
    const amount = Number(item.amount_cents) || 0;
    const monthly = Number(item.monthly_amount_cents)
        || (isRecurringOperationalCost(item.recurrence)
            ? operationalCostMonthlyCents(amount, item.frequency)
            : amount);
    const isProrated = isRecurringOperationalCost(item.recurrence)
        && item.frequency
        && item.frequency !== 'monthly';

    return (
        <Box>
            <Typography variant="body2" fontWeight={600}>
                {formatCurrencyInCents(amount)}
            </Typography>
            {isProrated ? (
                <Typography variant="caption" color="text.secondary" display="block">
                    {operationalCostFrequencyLabel(item.frequency)} · {formatCurrencyInCents(monthly)}/mês
                </Typography>
            ) : null}
        </Box>
    );
}

function CostTypeChips({ item }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            {isRecurringOperationalCost(item.recurrence) ? (
                <Chip label="Recorrente" size="small" color="primary" variant="outlined" />
            ) : (
                <Chip label="Pontual" size="small" variant="outlined" />
            )}
            {isRecurringOperationalCost(item.recurrence) && item.frequency && item.frequency !== 'monthly' ? (
                <Chip label={operationalCostFrequencyLabel(item.frequency)} size="small" variant="outlined" />
            ) : null}
        </Box>
    );
}

function ListOperationalCostsPage() {
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState(EMPTY_STATS);
    const [archivedTotal, setArchivedTotal] = useState(0);
    const [showArchived, setShowArchived] = useState(false);
    const [draftSearch, setDraftSearch] = useState('');
    const [draftRecurrence, setDraftRecurrence] = useState('');
    const [draftCategory, setDraftCategory] = useState('');
    const [queryFilters, setQueryFilters] = useState({ q: '', recurrence: '', category: '' });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_LIST_PAGE_SIZE);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [listVersion, setListVersion] = useState(0);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const hasActiveFilters = Boolean(queryFilters.q || queryFilters.recurrence || queryFilters.category);
    const isEmptyActive = !showArchived && !hasActiveFilters && (!Array.isArray(items) || items.length === 0);

    const fetchItems = useCallback(() => {
        setIsLoading(true);
        api.get('operational-costs', {
            params: {
                page: currentPage,
                limit: itemsPerPage,
                q: queryFilters.q || undefined,
                recurrence: queryFilters.recurrence || undefined,
                category: queryFilters.category || undefined,
                archived: showArchived ? 'archived' : undefined,
            },
        })
            .then((response) => {
                const data = response.data.data || {};
                setItems(data.items || []);
                setTotalPages(data.total_pages || 1);
                setStats(data.stats || EMPTY_STATS);
                setArchivedTotal(data.archived_total || 0);
                setDidNotLoad(false);
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [currentPage, itemsPerPage, queryFilters.category, queryFilters.q, queryFilters.recurrence, showArchived]);

    useListFetch(fetchItems, [
        currentPage,
        itemsPerPage,
        queryFilters.category,
        queryFilters.q,
        queryFilters.recurrence,
        showArchived,
        listVersion,
    ]);

    const handleApplyFilters = () => {
        setCurrentPage(1);
        setQueryFilters({
            q: draftSearch.trim(),
            recurrence: draftRecurrence,
            category: draftCategory,
        });
    };

    const handleClearFilters = () => {
        setDraftSearch('');
        setDraftRecurrence('');
        setDraftCategory('');
        setQueryFilters({ q: '', recurrence: '', category: '' });
        setCurrentPage(1);
    };

    const handleDelete = (costId) => {
        Swal.fire({
            title: 'Remover custo?',
            text: 'Esta ação não pode ser desfeita. Prefira arquivar se quiser só tirar da lista ativa.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sim, remover',
            cancelButtonText: 'Cancelar',
        }).then((result) => {
            if (result.isConfirmed) {
                setLoadingDelete(true);
                api.delete(`operational-costs/${costId}`)
                    .then(() => {
                        invalidatePlanUsage();
                        Swal.fire('Removido!', 'O custo foi removido.', 'success');
                        setListVersion((value) => value + 1);
                    })
                    .catch(() => {
                        Swal.fire('Erro!', 'Houve um problema ao remover o custo.', 'error');
                    })
                    .finally(() => {
                        setLoadingDelete(false);
                    });
            }
        });
    };

    const handleSetArchived = (item, archived) => {
        api.post(`operational-costs/${item.id}/archive`, { archived })
            .then(() => {
                invalidatePlanUsage();
                setListVersion((value) => value + 1);
            })
            .catch((error) => {
                const message = error?.response?.data?.message
                    || error?.response?.data?.errors?.form
                    || (archived ? 'Não foi possível arquivar o custo.' : 'Não foi possível desarquivar o custo. Verifique o limite do plano.');
                Swal.fire('Atenção', message, 'warning');
            });
    };

    const renderActions = (item) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
            {!showArchived ? (
                <>
                    <ActionBubbleTooltip title="Editar">
                        <IconButton
                            color="primary"
                            onClick={() => navigate(`/admin/edit-operational-cost/${item.id}`)}
                            aria-label="editar custo"
                        >
                            <Edit />
                        </IconButton>
                    </ActionBubbleTooltip>
                    <ActionBubbleTooltip title="Arquivar">
                        <IconButton
                            color="default"
                            onClick={() => handleSetArchived(item, true)}
                            aria-label="arquivar custo"
                        >
                            <ArchiveOutlined />
                        </IconButton>
                    </ActionBubbleTooltip>
                    <ActionBubbleTooltip title="Remover">
                        <IconButton
                            color="error"
                            onClick={() => handleDelete(item.id)}
                            aria-label="remover custo"
                            disabled={loadingDelete}
                        >
                            <Delete />
                        </IconButton>
                    </ActionBubbleTooltip>
                </>
            ) : (
                <UnarchiveAction item={item} onUnarchive={() => handleSetArchived(item, false)} />
            )}
        </Box>
    );

    if (isLoading) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />;
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to="/admin">
                    Dashboard
                </Link>
                <Typography color="text.primary">Custos operacionais</Typography>
            </Breadcrumbs>
            <br />
            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'stretch' : 'center',
                marginBottom: 2,
                gap: 2,
            }}>
                <Typography variant="h4" gutterBottom={!isMobile} sx={{ mb: isMobile ? 0 : undefined }}>
                    Custos operacionais
                </Typography>
                <Box sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: 1,
                    width: isMobile ? '100%' : 'auto',
                }}>
                    <PageTutorialButtons pageKey="operational_costs" fullWidth={isMobile} />
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setShowArchived((value) => !value);
                            setCurrentPage(1);
                        }}
                        fullWidth={isMobile}
                    >
                        {showArchived ? 'Ver ativos' : `Ver arquivados (${archivedTotal})`}
                    </Button>
                    {!showArchived ? <CreateOperationalCostButton isMobile={isMobile} /> : null}
                </Box>
            </Box>

            {!showArchived ? <OperationalCostsIntroBanner /> : (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Custos arquivados não entram no limite do plano nem nos totais do mês.
                    Desarquivar só é permitido se ainda houver cota disponível.
                </Alert>
            )}

            {!showArchived ? (
                <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Grid container spacing={2}>
                            <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Itens ativos
                                </Typography>
                                <Typography variant="subtitle1" fontWeight={700}>
                                    {stats.total_items || 0}
                                </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Recorrentes / mês
                                </Typography>
                                <Typography variant="subtitle1" fontWeight={700}>
                                    {formatCurrencyInCents(stats.recurring_total_cents)}
                                </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Pontuais deste mês
                                </Typography>
                                <Typography variant="subtitle1" fontWeight={700}>
                                    {formatCurrencyInCents(stats.one_time_total_cents)}
                                </Typography>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Impacto no mês
                                </Typography>
                                <Typography variant="subtitle1" fontWeight={700}>
                                    {formatCurrencyInCents(stats.month_total_cents
                                        || ((stats.recurring_total_cents || 0) + (stats.one_time_total_cents || 0)))}
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            ) : null}

            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                    Filtros
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            size="small"
                            fullWidth
                            label="Buscar"
                            placeholder="Nome, categoria ou notas"
                            value={draftSearch}
                            onChange={(event) => setDraftSearch(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    handleApplyFilters();
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="filter-recurrence-label">Recorrência</InputLabel>
                            <Select
                                labelId="filter-recurrence-label"
                                label="Recorrência"
                                value={draftRecurrence}
                                onChange={(event) => setDraftRecurrence(event.target.value)}
                            >
                                <MenuItem value="">Todas</MenuItem>
                                <MenuItem value={OPERATIONAL_COST_RECURRING}>Recorrente</MenuItem>
                                <MenuItem value={OPERATIONAL_COST_ONE_TIME}>Pontual</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="filter-category-label">Categoria</InputLabel>
                            <Select
                                labelId="filter-category-label"
                                label="Categoria"
                                value={draftCategory}
                                onChange={(event) => setDraftCategory(event.target.value)}
                            >
                                <MenuItem value="">Todas</MenuItem>
                                {OPERATIONAL_COST_CATEGORIES.map((category) => (
                                    <MenuItem key={category.value} value={category.value}>
                                        {category.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                    <Button variant="contained" onClick={handleApplyFilters}>
                        Filtrar
                    </Button>
                    {hasActiveFilters && (
                        <Button variant="outlined" color="secondary" onClick={handleClearFilters} startIcon={<ClearIcon />}>
                            Limpar filtros
                        </Button>
                    )}
                </Box>
            </Paper>

            {isEmptyActive ? (
                <Paper sx={{ p: 4, textAlign: 'center', mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Nenhum custo operacional cadastrado
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 520, mx: 'auto' }}>
                        Comece pelos fixos da farm (aluguel, utilidades, assinaturas). Depois inclua pontuais do mês quando aparecerem.
                    </Typography>
                    <CreateOperationalCostButton isMobile={false} />
                </Paper>
            ) : isMobile ? (
                <Stack spacing={1.5} sx={{ mb: 2 }}>
                    {Array.isArray(items) && items.length > 0 ? items.map((item) => (
                        <Card key={item.id} variant="outlined">
                            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography fontWeight={700}>{item.name}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {operationalCostCategoryLabel(item.category)}
                                        </Typography>
                                    </Box>
                                    {renderActions(item)}
                                </Box>
                                <CostTypeChips item={item} />
                                <Box sx={{ mt: 1.25 }}>
                                    {amountDisplay(item)}
                                </Box>
                                {item.notes ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        {item.notes}
                                    </Typography>
                                ) : null}
                            </CardContent>
                        </Card>
                    )) : (
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                                {showArchived
                                    ? 'Nenhum custo arquivado com os filtros atuais.'
                                    : 'Nenhum custo operacional cadastrado com os filtros atuais.'}
                            </Typography>
                        </Paper>
                    )}
                </Stack>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Custo</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Categoria</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Valor</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Array.isArray(items) && items.length > 0 ? items.map((item) => (
                                <TableRow key={item.id}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <Typography>{item.name}</Typography>
                                            <CostTypeChips item={item} />
                                        </Box>
                                        {item.notes ? (
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                {item.notes}
                                            </Typography>
                                        ) : null}
                                    </TableCell>
                                    <TableCell>{operationalCostCategoryLabel(item.category)}</TableCell>
                                    <TableCell>{amountDisplay(item)}</TableCell>
                                    <TableCell>{renderActions(item)}</TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={4}>
                                        <Typography variant="body2" color="text.secondary">
                                            {showArchived
                                                ? 'Nenhum custo arquivado com os filtros atuais.'
                                                : 'Nenhum custo operacional cadastrado com os filtros atuais.'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {!isEmptyActive ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                        mt: 3,
                    }}
                >
                    <Select
                        value={itemsPerPage}
                        onChange={(event) => {
                            setItemsPerPage(event.target.value);
                            setCurrentPage(1);
                        }}
                        variant="standard"
                    >
                        {LIST_PAGE_SIZE_OPTIONS.map((size) => (
                            <MenuItem key={size} value={size}>{size} por página</MenuItem>
                        ))}
                    </Select>
                    <CustomPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={(page) => {
                            setCurrentPage(page);
                        }}
                    />
                </Box>
            ) : null}
        </div>
    );
}

function UnarchiveAction({ item, onUnarchive }) {
    const quota = usePlanQuota(operationalCostQuotaKey(item.recurrence));
    if (quota.blocked) {
        return (
            <ActionBubbleTooltip title={quotaBlockedMessage(quota.entry)}>
                <span>
                    <IconButton color="primary" disabled aria-label="desarquivar bloqueado">
                        <UnarchiveOutlined />
                    </IconButton>
                </span>
            </ActionBubbleTooltip>
        );
    }
    return (
        <ActionBubbleTooltip title="Desarquivar">
            <IconButton color="primary" onClick={onUnarchive} aria-label="desarquivar custo">
                <UnarchiveOutlined />
            </IconButton>
        </ActionBubbleTooltip>
    );
}

export default ListOperationalCostsPage;
